import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import { useNavigate } from 'react-router-dom';
import LoadingModal from './components/modals/LoadingModal';
import PageCard from './components/PageCard';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import ConfirmModal from './components/modals/ConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import SubAcaoModal from './components/modalServices/SubAcaoModal';
import usePermissions from './components/hooks/usePermissions';
import useProgramaSituacao from './components/hooks/useProgramaSituacao';

function SubAcoes() {
  // Verificação de permissões
  const { podeAbrir, podeCriar, podeEditar, podeExcluir, podeExportar, carregando: carregandoPermissoes } = usePermissions('subacoes');
  
  // Verificação de situação do programa
  const { isProgramaAtivo } = useProgramaSituacao();
  
  const [subAcoes, setSubAcoes] = useState([]);
  const [atualizar, setAtualizar] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedAcoes, setSelectedAcoes] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [filtroGeral, setFiltroGeral] = useState('');
  const [expandedPrograms, setExpandedPrograms] = useState({});
  const [expandedActions, setExpandedActions] = useState({});
  const [isProgramaInativo, setIsProgramaInativo] = useState(false);

  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');

  // Filtrar dados
  const subAcoesFiltradas = [...subAcoes].filter(s => {
    if (!filtroGeral) return true;
    const termo = filtroGeral.trim().toLowerCase();
    const descricao = `${s.descricao || ''}`.toLowerCase();
    const programa = `${s.descricaoPrograma || ''}`.toLowerCase();
    const acao = `${s.descricaoAcao || ''}`.toLowerCase();

    return (
      descricao.includes(termo) ||
      programa.includes(termo) ||
      acao.includes(termo)
    );
  });

  // Não usa paginação com hierarquia

  // Hierarquizar sub-ações
  const hierarquizarSubAcoes = subAcoes => {
    if (!Array.isArray(subAcoes)) {
      return {};
    }

    const hierarquia = {};

    subAcoes.forEach(subAcao => {
      const programaId = subAcao.idPrograma;
      const acaoId = subAcao.idAcao;

      // Inicializar programa se não existir
      if (!hierarquia[programaId]) {
        hierarquia[programaId] = {
          programa: {
            id: programaId,
            descricao: subAcao.descricaoPrograma,
          },
          acoes: {},
        };
      }

      // Inicializar ação se não existir
      if (!hierarquia[programaId].acoes[acaoId]) {
        hierarquia[programaId].acoes[acaoId] = {
          acao: {
            id: acaoId,
            descricao: subAcao.descricaoAcao,
          },
          subAcoes: [],
        };
      }

      // Adicionar sub-ação
      hierarquia[programaId].acoes[acaoId].subAcoes.push(subAcao);
    });

    return hierarquia;
  };

  const subAcoesHierarquizadas = hierarquizarSubAcoes(subAcoesFiltradas);

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();

  useEffect(() => {
    if (!token) {
      navigate('/');
    }
    
    // Não carregar dados se não pode abrir o módulo
    if (!carregandoPermissoes && !podeAbrir) {
      return;
    }
    
    fetchAcao();
  }, [atualizar, podeAbrir, carregandoPermissoes]);

  function fetchAcao() {
    setLoading(true);
    axios
      .get(`${API_BASE_URL.SUB_ACAO}/find_all`, {
        params: { cnpjAssociacao: cnpj },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        setSubAcoes(result.data);
      })
      .catch(error => {
        console.error('Erro ao obter sub-ações:', error);
        navigate('/');
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const handleSelectRecord = record => {
    const recordKey = `${record.cnpjAssociacao}-${record.idPrograma}-${record.idAcao}-${record.id}`;

    if (selectedRecord && selectedRecord.key === recordKey) {
      setSelectedRecord(null);
      setIsProgramaInativo(false);
    } else {
      setSelectedRecord({ key: recordKey, data: record });
      // Verificar se o programa está encerrado
      const programaAtivo = isProgramaAtivo(record.idPrograma);
      setIsProgramaInativo(!programaAtivo);
    }
  };

  const getSelectedRecord = () => {
    return selectedRecord ? selectedRecord.data : null;
  };

  const hasSelectedRecord = selectedRecord !== null;

  function handleDelete(subAcao) {
    if (!subAcao) return;

    axios
      .delete(
        `${API_BASE_URL.SUB_ACAO}/delete/${subAcao.cnpjAssociacao}/${subAcao.idPrograma}/${subAcao.idAcao}/${subAcao.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then(result => {
        setAtualizar(result);
        setSelectedRecord(null);
        showDefaultModal('success', 'O registro foi excluído com sucesso!');
      })
      .catch(error => {
        console.error('Erro ao excluir sub-ação:', error);
        if (error.response) {
          const status = error.response.status;
          const message = error.response.data;
          if (status === 409) {
            showDefaultModal('error', message);
          } else {
            showDefaultModal('error', message);
          }
        } else {
          showDefaultModal('error', 'Erro de conexão com o servidor!');
        }
      });
  }

  const handleDeleteSelected = () => {
    const record = getSelectedRecord();
    if (record) {
      showConfirmModal('delete', () => handleDelete(record));
    }
  };

  const handleEditSelected = () => {
    const record = getSelectedRecord();
    if (record) {
      setSelectedAcoes(record);
      setTimeout(() => {
        setShowModal(true);
      }, 0);
    }
  };

  function handleNewAcao() {
    setSelectedAcoes(null);
    setShowModal(true);
  }

  function handleSuccess() {
    setAtualizar([]);
    setShowModal(false);
    setSelectedRecord(null);
  }

  // Funções para controlar dropdowns
  const toggleProgram = programId => {
    setExpandedPrograms(prev => ({
      ...prev,
      [programId]: !prev[programId],
    }));
  };

  const toggleAction = (programId, actionId) => {
    const key = `${programId}-${actionId}`;
    setExpandedActions(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Verificar se ainda está carregando permissões
  if (carregandoPermissoes || isLoading) {
    return <LoadingModal loading={true} />;
  }

  return (
    <PageCard
      title="Sub-Ações Cadastradas"
      icon="bi-list-nested"
      scrollable={false}
    >
      {!podeAbrir ? (
        <div className="alert alert-warning m-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>Acesso Negado</strong>
          <p className="mb-0 mt-2">
            Você não tem permissão para acessar o módulo de Sub-Ações.
          </p>
          <p className="mb-0 mt-2">
            <small>Entre em contato com o administrador do sistema.</small>
          </p>
          <button 
            className="btn btn-sm btn-outline-secondary mt-3"
            onClick={() => navigate('/home')}
          >
            <i className="bi bi-arrow-left me-1"></i>
            Voltar
          </button>
        </div>
      ) : (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

        <div className="d-flex flex-wrap gap-1 mb-2 p-2 bg-light rounded border align-items-center">
          <button
            onClick={handleNewAcao}
            className="btn btn-success btn-sm d-inline-flex align-items-center"
            disabled={!podeCriar}
            title={!podeCriar ? 'Você não tem permissão para criar sub-ações' : 'Nova sub-ação'}
          >
            <i className="bi bi-plus-lg me-1" />
            Novo
          </button>
          <button
            className="btn btn-primary btn-sm d-inline-flex align-items-center"
            onClick={handleEditSelected}
            disabled={!podeEditar || !hasSelectedRecord || isProgramaInativo}
            title={!podeEditar ? 'Você não tem permissão para editar' : !hasSelectedRecord ? 'Selecione um registro' : isProgramaInativo ? 'Programa ENCERRADO - não é possível editar' : 'Editar registro selecionado'}
          >
            <i className="bi bi-pencil me-1" />
            Editar
          </button>
          <div className="vr mx-1" />
          <button
            className="btn btn-danger btn-sm d-inline-flex align-items-center"
            onClick={handleDeleteSelected}
            disabled={!podeExcluir || !hasSelectedRecord || isProgramaInativo}
            title={!podeExcluir ? 'Você não tem permissão para excluir' : !hasSelectedRecord ? 'Selecione um registro' : isProgramaInativo ? 'Programa ENCERRADO - não é possível excluir' : 'Excluir registro selecionado'}
          >
            <i className="bi bi-trash me-1" />
            Excluir
          </button>

          {hasSelectedRecord && (
            <div className="d-flex align-items-center ms-1 px-3 py-1 bg-warning-subtle border border-warning rounded">
              <i className="bi bi-check-circle-fill text-warning me-2"></i>
              <small className="text-dark fw-medium">
                SELECIONADO:{' '}
                <span className="fw-bold">
                  {getSelectedRecord()?.descricao}
                </span>
              </small>
            </div>
          )}

          <div className="ms-auto d-flex align-items-center">
            <div
              className="input-group"
              style={{ maxWidth: 300, height: 31 }}
            >
              <span
                className="input-group-text bg-white border-end-0 px-2"
                style={{ height: 31 }}
              >
                <i className="bi bi-search text-muted" style={{ fontSize: 13 }} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm border-start-0"
                style={{
                  textTransform: 'uppercase',
                  height: 31,
                  fontSize: 13,
                }}
                placeholder="Pesquisar..."
                value={filtroGeral}
                onChange={e => setFiltroGeral(e.target.value.toUpperCase())}
              />
              {filtroGeral && (
                <button
                  className="btn btn-outline-secondary btn-sm border-start-0 d-flex align-items-center px-2"
                  style={{ height: 31 }}
                  onClick={() => setFiltroGeral('')}
                  title="Limpar pesquisa"
                  tabIndex={-1}
                >
                  <i className="bi bi-x-lg" style={{ fontSize: 11 }} />
                </button>
              )}
            </div>
          </div>
        </div>

        <div
          className="table-responsive-sm shadow-lg mb-0"
          style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
        >
          <table className="table table-hover table-striped table-sm small">
            <thead className="table-secondary sticky-top bg-light">
              <tr>
                <th scope="col">Hierarquia / Sub-Ação</th>
              </tr>
            </thead>
            <tbody>
              {!isLoading &&
                Object.values(subAcoesHierarquizadas)
                  .sort((a, b) => a.programa.id - b.programa.id)
                  .map(programaData => (
                    <React.Fragment key={programaData.programa.id}>
                      {/* Linha do Programa */}
                      <tr
                        className="table-success fw-bold cursor-pointer"
                        onClick={() => toggleProgram(programaData.programa.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td title="Programa - Nível superior da hierarquia">
                          <i
                            className={`bi ${
                              expandedPrograms[programaData.programa.id]
                                ? 'bi-folder-minus'
                                : 'bi-folder-plus'
                            } me-2`}
                          ></i>
                          {programaData.programa.descricao}
                        </td>
                      </tr>

                      {/* Ações do Programa */}
                      {expandedPrograms[programaData.programa.id] &&
                        Object.values(programaData.acoes)
                          .sort((a, b) => a.acao.id - b.acao.id)
                          .map(acaoData => (
                            <React.Fragment
                              key={`${programaData.programa.id}-${acaoData.acao.id}`}
                            >
                              {/* Linha da Ação */}
                              <tr
                                className="table-info fw-semibold cursor-pointer"
                                onClick={() =>
                                  toggleAction(
                                    programaData.programa.id,
                                    acaoData.acao.id
                                  )
                                }
                                style={{ cursor: 'pointer' }}
                              >
                                <td
                                  className="ps-4"
                                  title="Ação - Segundo nível da hierarquia"
                                >
                                  <i
                                    className={`bi ${
                                      expandedActions[
                                        `${programaData.programa.id}-${acaoData.acao.id}`
                                      ]
                                        ? 'bi-folder-minus'
                                        : 'bi-folder-plus'
                                    } me-2`}
                                  ></i>
                                  {acaoData.acao.descricao}
                                </td>
                              </tr>

                              {/* Sub-ações da Ação */}
                              {expandedActions[
                                `${programaData.programa.id}-${acaoData.acao.id}`
                              ] &&
                                acaoData.subAcoes
                                  .sort((a, b) => a.id - b.id)
                                  .map(subAcao => {
                                    const recordKey = `${subAcao.cnpjAssociacao}-${subAcao.idPrograma}-${subAcao.idAcao}-${subAcao.id}`;
                                    const isSelected =
                                      selectedRecord &&
                                      selectedRecord.key === recordKey;

                                    return (
                                      <tr
                                        key={recordKey}
                                        className={`${
                                          isSelected ? 'table-warning' : ''
                                        } cursor-pointer`}
                                        onClick={() =>
                                          handleSelectRecord(subAcao)
                                        }
                                        style={{ cursor: 'pointer' }}
                                      >
                                        <td
                                          className="ps-5"
                                          title="Sub-Ação - Elemento executável da hierarquia"
                                        >
                                          <i className="bi bi-file-earmark me-2 text-primary"></i>
                                          {subAcao.descricao}
                                        </td>
                                      </tr>
                                    );
                                  })}
                            </React.Fragment>
                          ))}
                    </React.Fragment>
                  ))}
            </tbody>
          </table>
        </div>

        {/* Rodapé com total de sub-ações */}
        <div className="d-flex justify-content-center mt-2 mb-2">
          <div className="d-flex align-items-center px-3 py-1 bg-info-subtle border border-info rounded">
            <i className="bi bi-info-circle-fill text-info me-2"></i>
            <small className="text-dark fw-medium">
              Total: <span className="fw-bold">{subAcoesFiltradas.length}</span>{' '}
              sub-ações
            </small>
          </div>
        </div>
      </div>
      )}

      <ConfirmModal {...setConfirmModal} />
      <DefaultModal {...setDefaultModal} />
      
      {/* Modal para edição/criação de sub-ação */}
      <SubAcaoModal
        show={showModal}
        onHide={() => setShowModal(false)}
        subacao={selectedAcoes}
        onSuccess={handleSuccess}
      />
    </PageCard>
  );
}

export default SubAcoes;
